import analytics from "analytics";
import { getStorageKey } from "contexts/GuidedSignUpProvider";
import {
  AddPaymentMethodResponseCamelCase,
  CheckoutSession,
  Invoice,
  PurchaseSubscriptionResponse,
} from "lib/api/checkout";
import { Subscription } from "lib/api/subscription";
import { useRouter } from "next/router";
import useWindowStorage from "utils/hooks/useWindowStorage";
import { isAPIError } from "lib/api/base";
import { facebookPixelTrackAddPaymentInfo } from "utils/facebook/facebookPixelTrackEvent";
import { useCallback, useContext, useState } from "react";
import { CheckoutContext } from "contexts/CheckoutProvider";
import {
  getEstimatedLTV,
  getSubscriptionFromInvoice,
  getSubscriptionPurchaseInvoice,
} from "components/Payment/paymentUtil";
import {
  OnToken,
  PaymentMethodOptions,
  PaymentMethod,
} from "components/Payment/paymentTypes";
import { NormalizedOrderSummary } from "components/Step/stepTypes";

export interface UsePaymentArgs {
  checkoutSession: CheckoutSession;
  contentfulQuizModelRaw: any;
  endSubmitStateOnSuccess?: boolean;
  onPurchaseSuccess?: (
    invoice: Invoice,
    subscription: Subscription | null
  ) => void;
  onToken?: OnToken;
  orderSummary: NormalizedOrderSummary;
  paymentMethod: PaymentMethod;
  phoneNumber?: string | null;
}

const usePayment = ({
  checkoutSession,
  contentfulQuizModelRaw,
  endSubmitStateOnSuccess = false,
  onPurchaseSuccess,
  onToken,
  orderSummary,
  paymentMethod,
  phoneNumber,
}: UsePaymentArgs): PaymentMethodOptions => {
  const router = useRouter();
  const [_, { removeItem }] = useWindowStorage();
  const { addPaymentMethod, purchaseSubscription } =
    useContext(CheckoutContext);

  const [error, setError] = useState("");
  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const [submitting, setSubmitting] = useState(false);

  // charge reflects applied credit
  const orderTotal = orderSummary?.charge?.value || orderSummary?.total?.value;
  const redeemingGift = Boolean(checkoutSession?.giftCode);

  const handlePurchaseSuccess = useCallback(
    async (res: PurchaseSubscriptionResponse, estimatedLTV?: number) => {
      if (redeemingGift) {
        setSubmitting(false);
        return;
      }

      const invoice = getSubscriptionPurchaseInvoice(res);

      analytics.subscriptionPurchased({
        estimatedLTV: estimatedLTV ? String(estimatedLTV) : undefined,
        invoice,
        url: router?.route,
      });

      // after successful purchase, start user fresh in the guided sign up flow
      const guidedSignUpStorageKey = getStorageKey({
        contentfulQuizModelSlug: contentfulQuizModelRaw?.fields?.slug,
      });

      if (guidedSignUpStorageKey) {
        removeItem(guidedSignUpStorageKey);
      }

      // includes data the UI needs that the purchase response lacks
      const subscription = await getSubscriptionFromInvoice(invoice);

      /**
       * In all current cases we route after submission succeeds. For that case we
       * keep the submitting state on so the interstitial does not momentarily dismiss
       * before the routing occurs. `endSubmitStateOnSuccess` allows usage to change
       * this behavior.
       */
      if (endSubmitStateOnSuccess) {
        setSubmitting(false);
      }
      onPurchaseSuccess?.(invoice, subscription);
    },
    [
      contentfulQuizModelRaw?.fields?.slug,
      endSubmitStateOnSuccess,
      onPurchaseSuccess,
      redeemingGift,
      removeItem,
      router?.route,
    ]
  );

  const handleToken: OnToken = useCallback(
    async (args) => {
      setSubmitting(true);
      const { token } = args;

      if (paymentMethod !== "default" && token !== undefined) {
        const res = (await addPaymentMethod?.(
          paymentMethod,
          token
        )) as AddPaymentMethodResponseCamelCase;

        if (isAPIError(res)) {
          setSubmitting(false);
          return res;
        }

        const paymentMethodId = String(res?.newPaymentMethodId);
        if (paymentMethodId) {
          facebookPixelTrackAddPaymentInfo({
            paymentMethodId,
            url: router?.route,
          });
        }
      }

      const estimatedLTV = await getEstimatedLTV({ contentfulQuizModelRaw });
      await purchaseSubscription?.(
        (res) => handlePurchaseSuccess(res, estimatedLTV || undefined),
        {
          skipAddress: !redeemingGift,
          estimatedLTV: estimatedLTV ? String(estimatedLTV) : undefined,
          suppressAutoRoute: true,
          phoneNumber: phoneNumber ?? null,
        }
      );

      return onToken?.(args);
    },
    [
      addPaymentMethod,
      contentfulQuizModelRaw,
      handlePurchaseSuccess,
      onToken,
      paymentMethod,
      purchaseSubscription,
      redeemingGift,
      router.route,
      phoneNumber,
    ]
  );

  return {
    error,
    handleError,
    handleToken,
    orderTotal,
    redeemingGift,
    submitting,
  };
};

export default usePayment;
